// What a captain portrait is drawn from (R1180, ui-map-interface.md §12).
//
// The derived face comes from the NAME and the two TALENTS, the keys the
// hub and the game share, so one captain shows one face on both surfaces.
// The Captain Designer freezes that face as a stored seed and gender before
// a rename (R1240), and `storedLooks` reads the stored pair. The Go port
// `game.PortraitSeed` must give the same answer: `seed.test.ts` and the Go
// test read one golden fixture.

import { CAPTAIN_FIRST_FEMALE, DECLINE_AT_AGE, PEAK_AT_AGE } from '../../gen/balance';
import { CaptainPhase } from '../../gen/proto/aquaphobia';
import { mix32 } from './rng';

/** A captain's career phase, as the game's `Captain.phase` spells it. */
export type Phase = 'rise' | 'peak' | 'decline';

const FEMALE: ReadonlySet<string> = new Set(CAPTAIN_FIRST_FEMALE);
const ENCODER = new TextEncoder();

/**
 * The 32-bit seed of one captain: FNV-1a over the UTF-8 bytes of
 * `name|a,b`, with the talent pair sorted so its order never matters, and
 * then the mockup's finalizer.
 */
export function portraitSeed(name: string, talents: readonly number[]): number {
  const pair = [...talents].sort((a, b) => a - b).join(',');
  let h = 0x811c9dc5;
  for (const b of ENCODER.encode(`${name}|${pair}`)) {
    h ^= b;
    h = Math.imul(h, 0x01000193);
  }
  return mix32(h >>> 0);
}

/**
 * True when the first name is a woman's (names.toml
 * `captain_first_female`). A name not on the list draws a man.
 */
export function isFemale(name: string): boolean {
  return FEMALE.has(name.trim().split(/\s+/)[0] ?? '');
}

/** The portrait gender codes of the meta row and the wire (R1240):
 *  0 derives the sex from the first name, 1 is male and 2 is female. */
export const GENDER_MALE = 1;
export const GENDER_FEMALE = 2;

/**
 * The stored face of a captain as the optional part of a `PortraitSpec`.
 * A seed of 0 and a gender of 0 mean "not stored", and the face then
 * comes from the name and the talents (R1240, monetization.md §3.1).
 */
export function storedLooks(seed: number, gender: number): { seed?: number; female?: boolean } {
  const out: { seed?: number; female?: boolean } = {};
  if (Number.isFinite(seed) && seed > 0) out.seed = seed >>> 0;
  if (gender === GENDER_MALE) out.female = false;
  else if (gender === GENDER_FEMALE) out.female = true;
  return out;
}

/**
 * The career phase of an age in whole years (numbers.md §12, R1170). The
 * hub roster sends the age and no phase, so the hub reads the phase here,
 * off the two thresholds of the compile-time lane.
 */
export function phaseOfAge(age: number): Phase {
  const n = Number.isFinite(age) ? age : 0;
  return n >= DECLINE_AT_AGE ? 'decline' : n >= PEAK_AT_AGE ? 'peak' : 'rise';
}

/** The phase the game's `Captain.phaseKind` states, or the phase of the age
 *  when the kind is unspecified. The `phase` string is a translated label,
 *  so the portrait never reads it. */
export function phaseFromKind(kind: CaptainPhase, age: number): Phase {
  switch (kind) {
    case CaptainPhase.CAPTAIN_PHASE_RISE: return 'rise';
    case CaptainPhase.CAPTAIN_PHASE_PEAK: return 'peak';
    case CaptainPhase.CAPTAIN_PHASE_DECLINE: return 'decline';
    default: return phaseOfAge(age);
  }
}
